import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useUpload } from "@/hooks/use-upload";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Clock, Building, Lock, Loader2, Image, Upload, X, Bell, Key, Smartphone, Mail, MessageSquare, Truck, Copy, Check, AlertCircle, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

const settingsFormSchema = z.object({
  orderWindowOpen: z.string().regex(/^\d{2}:\d{2}$/, "Ange tid i format HH:MM"),
  orderWindowClose: z.string().regex(/^\d{2}:\d{2}$/, "Ange tid i format HH:MM"),
  orderMode: z.enum(["normal", "closed"]),
  closedOrderMessage: z.string().max(1000, "Meddelandet får vara högst 1000 tecken"),
  companyName: z.string().optional(),
  companyAddress: z.string().optional(),
  companyPhone: z.string().optional(),
  companyEmail: z.string().email().optional().or(z.literal("")),
});

type SettingsFormData = z.infer<typeof settingsFormSchema>;

const passwordFormSchema = z.object({
  currentPassword: z.string().min(1, "Nuvarande lösenord krävs"),
  newPassword: z.string().min(6, "Nytt lösenord måste vara minst 6 tecken"),
  confirmPassword: z.string().min(1, "Bekräfta lösenordet"),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Lösenorden matchar inte",
  path: ["confirmPassword"],
});

type PasswordFormData = z.infer<typeof passwordFormSchema>;

function PasswordChangeCard() {
  const { toast } = useToast();
  const [isChanging, setIsChanging] = useState(false);

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handlePasswordChange = async (data: PasswordFormData) => {
    setIsChanging(true);
    try {
      const response = await apiRequest("POST", "/api/admin-auth/change-password", {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Lösenord ändrat",
          description: "Ditt lösenord har uppdaterats.",
        });
        passwordForm.reset();
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Fel",
        description: error.message || "Kunde inte ändra lösenord",
      });
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Ändra lösenord
        </CardTitle>
        <CardDescription>
          Uppdatera ditt admin-lösenord
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...passwordForm}>
          <form onSubmit={passwordForm.handleSubmit(handlePasswordChange)} className="space-y-4">
            <FormField
              control={passwordForm.control}
              name="currentPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nuvarande lösenord</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" data-testid="input-current-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={passwordForm.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nytt lösenord</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" data-testid="input-new-password" />
                  </FormControl>
                  <FormDescription>Minst 6 tecken</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={passwordForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bekräfta nytt lösenord</FormLabel>
                  <FormControl>
                    <Input {...field} type="password" data-testid="input-confirm-password" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isChanging} data-testid="button-change-password">
              {isChanging ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Ändrar...
                </>
              ) : (
                "Ändra lösenord"
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

export default function AdminSettings() {
  const { toast } = useToast();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [pwaIconUrl, setPwaIconUrl] = useState<string | null>(null);

  const { data: settings, isLoading } = useQuery<Record<string, string>>({
    queryKey: ["/api/admin/settings"],
  });

  const { uploadFile, isUploading } = useUpload({
    onSuccess: (response) => {
      const publicUrl = `/api/uploads${response.objectPath}`;
      setLogoUrl(publicUrl);
      saveLogo(publicUrl);
      toast({ title: "Logotyp uppladdad!" });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte ladda upp logotypen.", variant: "destructive" });
    },
  });

  const { uploadFile: uploadPwaIcon, isUploading: isUploadingPwa } = useUpload({
    onSuccess: (response) => {
      const publicUrl = `/api/uploads${response.objectPath}`;
      setPwaIconUrl(publicUrl);
      savePwaIcon(publicUrl);
      toast({ title: "PWA-ikon uppladdad!" });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte ladda upp ikonen.", variant: "destructive" });
    },
  });

  const saveLogo = async (url: string) => {
    try {
      await apiRequest("POST", "/api/admin/settings", { companyLogo: url });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    } catch (error) {
      console.error("Failed to save logo:", error);
    }
  };

  const savePwaIcon = async (url: string) => {
    try {
      await apiRequest("POST", "/api/admin/settings", { pwa_icon_url: url });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    } catch (error) {
      console.error("Failed to save PWA icon:", error);
    }
  };

  const removeLogo = async () => {
    try {
      await apiRequest("POST", "/api/admin/settings", { companyLogo: "" });
      setLogoUrl(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      toast({ title: "Logotyp borttagen" });
    } catch (error) {
      toast({ title: "Fel", description: "Kunde inte ta bort logotypen.", variant: "destructive" });
    }
  };

  const removePwaIcon = async () => {
    try {
      await apiRequest("POST", "/api/admin/settings", { pwa_icon_url: "" });
      setPwaIconUrl(null);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      toast({ title: "PWA-ikon borttagen", description: "Standardikonen används nu." });
    } catch (error) {
      toast({ title: "Fel", description: "Kunde inte ta bort ikonen.", variant: "destructive" });
    }
  };

  const form = useForm<SettingsFormData>({
    resolver: zodResolver(settingsFormSchema),
    defaultValues: {
      orderWindowOpen: "06:00",
      orderWindowClose: "14:00",
      orderMode: "normal",
      closedOrderMessage: "Vi tar inte emot beställningar just nu.",
      companyName: "",
      companyAddress: "",
      companyPhone: "",
      companyEmail: "",
    },
  });

  useEffect(() => {
    if (settings) {
      form.reset({
        orderWindowOpen: settings.orderWindowOpen || "06:00",
        orderWindowClose: settings.orderWindowClose || "14:00",
        orderMode: settings.orderMode === "closed" ? "closed" : "normal",
        closedOrderMessage: settings.closedOrderMessage || "Vi tar inte emot beställningar just nu.",
        companyName: settings.companyName || "",
        companyAddress: settings.companyAddress || "",
        companyPhone: settings.companyPhone || "",
        companyEmail: settings.companyEmail || "",
      });
      if (settings.companyLogo) {
        setLogoUrl(settings.companyLogo);
      }
      if (settings.pwa_icon_url) {
        setPwaIconUrl(settings.pwa_icon_url);
      }
    }
  }, [settings, form]);

  const updateMutation = useMutation({
    mutationFn: (data: SettingsFormData) => apiRequest("POST", "/api/admin/settings", data),
    onSuccess: () => {
      toast({ title: "Inställningar sparade!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/settings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/settings/order-window"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte spara inställningar.", variant: "destructive" });
    },
  });

  const onSubmit = (data: SettingsFormData) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Inställningar</h1>
        <p className="text-muted-foreground">Hantera systemets inställningar</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Beställningsfönster
              </CardTitle>
              <CardDescription>
                Ange tider för när kunder kan lägga beställningar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="orderWindowOpen"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Öppnar</FormLabel>
                      <FormControl>
                        <Input {...field} type="time" data-testid="input-order-open" />
                      </FormControl>
                      <FormDescription>När beställningar öppnar</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="orderWindowClose"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Stänger</FormLabel>
                      <FormControl>
                        <Input {...field} type="time" data-testid="input-order-close" />
                      </FormControl>
                      <FormDescription>När beställningar stänger</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="orderMode"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between gap-4 rounded-lg border p-4">
                    <div className="space-y-1">
                      <FormLabel>Stäng beställningar helt</FormLabel>
                      <FormDescription>
                        {field.value === "closed"
                          ? "Kunder och säljare kan inte skapa några beställningar."
                          : "Beställningar följer öppettiderna ovan. Framtida beställningar är möjliga när fönstret är stängt."}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value === "closed"}
                        onCheckedChange={(checked) => field.onChange(checked ? "closed" : "normal")}
                        aria-label="Stäng beställningar helt"
                        data-testid="switch-ordering-disabled"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="closedOrderMessage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Meddelande när beställningar är helt stängda</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Skriv meddelandet som kunderna ska se"
                        rows={3}
                        data-testid="textarea-closed-order-message"
                      />
                    </FormControl>
                    <FormDescription>Visas för kunder i stället för beställningsformuläret.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="h-5 w-5" />
                Logotyp
              </CardTitle>
              <CardDescription>
                Ladda upp företagets logotyp som visas på leveranssedlar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                {logoUrl ? (
                  <div className="relative">
                    <img
                      src={logoUrl}
                      alt="Företagslogotyp"
                      className="h-20 w-auto max-w-[200px] object-contain rounded border"
                      data-testid="img-company-logo"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 scale-75"
                      onClick={removeLogo}
                      data-testid="button-remove-logo"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-20 w-32 border-2 border-dashed rounded flex items-center justify-center bg-muted/50">
                    <Image className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploading}
                    onClick={() => document.getElementById("logo-upload")?.click()}
                    data-testid="button-upload-logo"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Laddar upp...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Välj logotyp
                      </>
                    )}
                  </Button>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadFile(file);
                    }}
                    data-testid="input-logo-upload"
                  />
                  <p className="text-xs text-muted-foreground">
                    JPG, PNG eller WebP. Max 10 MB.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                App-ikon (PWA)
              </CardTitle>
              <CardDescription>
                Ikon som visas när appen installeras på telefon eller dator
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start gap-4">
                {pwaIconUrl ? (
                  <div className="relative">
                    <img
                      src={pwaIconUrl}
                      alt="PWA-ikon"
                      className="h-20 w-20 object-contain rounded-xl border"
                      data-testid="img-pwa-icon"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 scale-75"
                      onClick={removePwaIcon}
                      data-testid="button-remove-pwa-icon"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="h-20 w-20 border-2 border-dashed rounded-xl flex items-center justify-center bg-muted/50">
                    <Smartphone className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isUploadingPwa}
                    onClick={() => document.getElementById("pwa-icon-upload")?.click()}
                    data-testid="button-upload-pwa-icon"
                  >
                    {isUploadingPwa ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Laddar upp...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Välj ikon
                      </>
                    )}
                  </Button>
                  <input
                    id="pwa-icon-upload"
                    type="file"
                    accept="image/png"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadPwaIcon(file);
                    }}
                    data-testid="input-pwa-icon-upload"
                  />
                  <p className="text-xs text-muted-foreground">
                    PNG-bild, helst 512x512 px. Visas som app-ikon på mobil och dator.
                  </p>
                  {!pwaIconUrl && (
                    <p className="text-xs text-muted-foreground">
                      Standardikonen används om ingen egen ikon laddas upp.
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5" />
                Företagsinformation
              </CardTitle>
              <CardDescription>
                Information som visas på leveranssedlar
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="companyName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Företagsnamn</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ditt företagsnamn" data-testid="input-company-name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyAddress"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adress</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Gatuadress, postnummer, stad" data-testid="input-company-address" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="companyPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Telefon</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" data-testid="input-company-phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="companyEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-post</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" data-testid="input-company-email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Button type="submit" disabled={updateMutation.isPending} data-testid="button-save-settings">
            {updateMutation.isPending ? "Sparar..." : "Spara inställningar"}
          </Button>
        </form>
      </Form>

      <PasswordChangeCard />

      <CommSettingsCard />

      <ExternalApiKeyCard />

      <LeveransoCard />

      <PushNotificationCard />
    </div>
  );
}

function LeveransoCard() {
  const { toast } = useToast();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [apiKeyHeader, setApiKeyHeader] = useState("X-API-Key");
  const [showApiKey, setShowApiKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message?: string; error?: string } | null>(null);

  const { data: settings } = useQuery<Record<string, string>>({ queryKey: ["/api/admin/settings"] });
  const { data: externalSettings } = useQuery<Record<string, string>>({ queryKey: ["/api/admin/settings"] });

  useEffect(() => {
    if (settings) {
      if (settings.leveranso_webhook_url) setWebhookUrl(settings.leveranso_webhook_url);
      if (settings.leveranso_api_key) setApiKey(settings.leveranso_api_key);
      if (settings.leveranso_api_key_header) setApiKeyHeader(settings.leveranso_api_key_header || "X-API-Key");
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/settings", {
      leveranso_webhook_url: webhookUrl,
      leveranso_api_key: apiKey,
      leveranso_api_key_header: apiKeyHeader,
    }),
    onSuccess: () => {
      toast({ title: "Leveranso-inställningar sparade" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      setTestResult(null);
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte spara inställningar.", variant: "destructive" });
    },
  });

  const testMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/leveranso/test", {}),
    onSuccess: (data: any) => setTestResult({ ok: true, message: data.message }),
    onError: (err: any) => setTestResult({ ok: false, error: err?.message || "Anslutning misslyckades" }),
  });

  const inboundUrl = `${window.location.origin}/api/external/orders/{ORDER_ID}/status`;
  const inboundKey = externalSettings?.external_api_key || "";

  const copyInboundUrl = () => {
    navigator.clipboard.writeText(inboundUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Leveranso-integration
        </CardTitle>
        <CardDescription>
          Synkronisera orderstatus mellan Rätt Order och Leveranso i båda riktningarna
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <p className="text-sm font-medium">Inkommande (Leveranso → Rätt Order)</p>
          <p className="text-xs text-muted-foreground">
            Konfigurera Leveranso att skicka statusuppdateringar till denna URL. Använd HTTP-metod PATCH och inkludera API-nyckeln.
          </p>
          <div className="bg-muted rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2">
              <code className="text-xs flex-1 break-all font-mono">{inboundUrl}</code>
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={copyInboundUrl}
                data-testid="button-copy-webhook-url"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="border-t pt-2 space-y-1">
              <p className="text-xs text-muted-foreground">Header: <code className="font-mono">X-API-Key: {inboundKey ? "••••••••" : "(ange API-nyckel under Externt API)"}</code></p>
              <p className="text-xs text-muted-foreground">Body: <code className="font-mono">{`{ "status": "delivered" }`}</code></p>
              <p className="text-xs text-muted-foreground">Giltiga statusar: pending, confirmed, in_production, packed, delivered, levererad</p>
            </div>
          </div>
        </div>

        <div className="border-t pt-4 space-y-3">
          <p className="text-sm font-medium">Utgående (Rätt Order → Leveranso)</p>
          <p className="text-xs text-muted-foreground">
            Rätt Order skickar automatiskt en POST-begäran till Leveranso när orderstatus ändras härifrån.
          </p>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-medium">Leveranso webhook-URL</label>
              <Input
                value={webhookUrl}
                onChange={e => setWebhookUrl(e.target.value)}
                placeholder="https://api.leveranso.com/webhooks/orders"
                data-testid="input-leveranso-webhook-url"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">API-nyckel (Leveranso)</label>
                <div className="flex gap-2">
                  <Input
                    type={showApiKey ? "text" : "password"}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="Leveransos API-nyckel"
                    data-testid="input-leveranso-api-key"
                  />
                  <Button variant="outline" size="sm" onClick={() => setShowApiKey(v => !v)} data-testid="button-toggle-leveranso-key">
                    {showApiKey ? "Dölj" : "Visa"}
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Header-namn för nyckel</label>
                <Input
                  value={apiKeyHeader}
                  onChange={e => setApiKeyHeader(e.target.value)}
                  placeholder="X-API-Key"
                  data-testid="input-leveranso-key-header"
                />
              </div>
            </div>
          </div>

          {testResult && (
            <div className={`flex items-start gap-2 p-2 rounded-md text-sm ${testResult.ok ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`} data-testid="leveranso-test-result">
              {testResult.ok
                ? <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                : <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />}
              <p className="text-xs">{testResult.ok ? testResult.message : testResult.error}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => testMutation.mutate()}
              disabled={testMutation.isPending || !webhookUrl}
              data-testid="button-test-leveranso"
            >
              {testMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
              {testMutation.isPending ? "Testar..." : "Testa anslutning"}
            </Button>
            <Button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              data-testid="button-save-leveranso"
            >
              {saveMutation.isPending ? "Sparar..." : "Spara"}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CommSettingsCard() {
  const { toast } = useToast();
  const [fields, setFields] = useState<Record<string, string>>({});
  const [testEmail, setTestEmail] = useState("");
  const [testSms, setTestSms] = useState("");
  const [testWa, setTestWa] = useState("");

  const { data: settings } = useQuery<Record<string, string>>({ queryKey: ["/api/admin/settings"] });

  useEffect(() => {
    if (!settings) return;
    const keys = [
      "smtp_host", "smtp_port", "smtp_secure", "smtp_user", "smtp_password", "smtp_from_email", "smtp_from_name",
      "sms_api_username", "sms_api_password", "sms_sender",
      "whatsapp_business_account_id", "whatsapp_phone_number_id", "whatsapp_access_token", "whatsapp_template_name", "whatsapp_language_code",
    ];
    const vals: Record<string, string> = {};
    keys.forEach(k => { vals[k] = settings[k] || ""; });
    setFields(vals);
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, string>) => apiRequest("POST", "/api/admin/settings", data),
    onSuccess: () => {
      toast({ title: "Kommunikationsinställningar sparade!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte spara.", variant: "destructive" }),
  });

  const testEmailMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/communication/test/email", { to: testEmail }),
    onSuccess: () => toast({ title: "Testmejl skickat!" }),
    onError: (err: any) => toast({ title: "Fel", description: err?.message || "Kunde inte skicka testmejl.", variant: "destructive" }),
  });

  const testSmsMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/communication/test/sms", { to: testSms }),
    onSuccess: (data: any) => {
      if (data?.ok === false) {
        toast({ title: "SMS misslyckades", description: data.error || "Okänt fel från SMS-tjänsten.", variant: "destructive" });
      } else {
        toast({ title: "Test-SMS skickat!" });
      }
    },
    onError: (err: any) => toast({ title: "Fel", description: err?.message || "Kunde inte skicka test-SMS.", variant: "destructive" }),
  });

  const testWaMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/communication/test/whatsapp", { to: testWa }),
    onSuccess: () => toast({ title: "Test-WhatsApp skickat!" }),
    onError: (err: any) => toast({ title: "Fel", description: err?.message || "Kunde inte skicka test-WhatsApp.", variant: "destructive" }),
  });

  const set = (k: string, v: string) => setFields(prev => ({ ...prev, [k]: v }));
  const save = (keys: string[]) => {
    const payload: Record<string, string> = {};
    keys.forEach(k => { if (fields[k] !== undefined) payload[k] = fields[k]; });
    saveMutation.mutate(payload);
  };

  const isConfigured = (v: string) => v && v !== "***configured***";

  return (
    <div className="space-y-4">
      {/* SMTP */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="h-5 w-5" />
            E-post (SMTP)
          </CardTitle>
          <CardDescription>Konfiguration för utgående e-post (t.ex. följesedlar, kampanjer)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="space-y-1 sm:col-span-2">
              <label className="text-sm font-medium">SMTP-server</label>
              <Input value={fields.smtp_host || ""} onChange={e => set("smtp_host", e.target.value)} placeholder="smtp.gmail.com" data-testid="input-smtp-host" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Port</label>
              <Input value={fields.smtp_port || ""} onChange={e => set("smtp_port", e.target.value)} placeholder="587" data-testid="input-smtp-port" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Användarnamn</label>
              <Input value={fields.smtp_user || ""} onChange={e => set("smtp_user", e.target.value)} placeholder="user@example.com" data-testid="input-smtp-user" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Lösenord</label>
              <Input type="password" value={fields.smtp_password || ""} onChange={e => set("smtp_password", e.target.value)} placeholder={isConfigured(settings?.smtp_password || "") ? "••••••••" : "App-lösenord"} data-testid="input-smtp-password" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={fields.smtp_secure === "true"}
              onCheckedChange={checked => set("smtp_secure", checked ? "true" : "false")}
              data-testid="switch-smtp-secure"
            />
            <div>
              <p className="text-sm font-medium">SSL/TLS (port 465)</p>
              <p className="text-xs text-muted-foreground">Aktivera för SSL-port, inaktivera för STARTTLS (port 587)</p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Avsändaradress</label>
              <Input value={fields.smtp_from_email || ""} onChange={e => set("smtp_from_email", e.target.value)} placeholder="noreply@example.com" data-testid="input-smtp-from-email" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Avsändarnamn</label>
              <Input value={fields.smtp_from_name || ""} onChange={e => set("smtp_from_name", e.target.value)} placeholder="Reef Bageri" data-testid="input-smtp-from-name" />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap pt-1">
            <Button size="sm" onClick={() => save(["smtp_host","smtp_port","smtp_secure","smtp_user","smtp_password","smtp_from_email","smtp_from_name"])} disabled={saveMutation.isPending} data-testid="button-save-smtp">
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Spara SMTP
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap pt-1 border-t">
            <label className="text-sm font-medium w-full">Skicka testmejl</label>
            <Input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@example.com" className="max-w-xs" data-testid="input-test-email" />
            <Button size="sm" variant="outline" onClick={() => testEmailMutation.mutate()} disabled={testEmailMutation.isPending || !testEmail} data-testid="button-test-email">
              {testEmailMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Skicka test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SMS */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Smartphone className="h-5 w-5" />
            SMS (46elks)
          </CardTitle>
          <CardDescription>API-uppgifter för 46elks SMS-tjänst</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">API-användarnamn</label>
              <Input value={fields.sms_api_username || ""} onChange={e => set("sms_api_username", e.target.value)} placeholder="u..." data-testid="input-sms-username" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">API-lösenord</label>
              <Input type="password" value={fields.sms_api_password || ""} onChange={e => set("sms_api_password", e.target.value)} placeholder={isConfigured(settings?.sms_api_password || "") ? "••••••••" : "API-lösenord"} data-testid="input-sms-password" />
            </div>
          </div>
          <div className="space-y-1 max-w-xs">
            <label className="text-sm font-medium">Avsändarnamn</label>
            <Input value={fields.sms_sender || ""} onChange={e => set("sms_sender", e.target.value)} placeholder="ReefBageri" data-testid="input-sms-sender" />
          </div>
          <div className="flex gap-2 flex-wrap pt-1">
            <Button size="sm" onClick={() => save(["sms_api_username","sms_api_password","sms_sender"])} disabled={saveMutation.isPending} data-testid="button-save-sms">
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Spara SMS
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap pt-1 border-t">
            <label className="text-sm font-medium w-full">Skicka test-SMS</label>
            <Input value={testSms} onChange={e => setTestSms(e.target.value)} placeholder="+46701234567" className="max-w-xs" data-testid="input-test-sms" />
            <Button size="sm" variant="outline" onClick={() => testSmsMutation.mutate()} disabled={testSmsMutation.isPending || !testSms} data-testid="button-test-sms">
              {testSmsMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Skicka test
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-5 w-5" />
            WhatsApp (Meta Cloud API)
          </CardTitle>
          <CardDescription>Konfiguration för WhatsApp Business via Meta Cloud API</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium">WhatsApp Business Account ID</label>
            <Input value={fields.whatsapp_business_account_id || ""} onChange={e => set("whatsapp_business_account_id", e.target.value)} placeholder="123456789012345" data-testid="input-wa-business-id" />
            <p className="text-xs text-muted-foreground">Finns i Meta Business Manager under WhatsApp-konton</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Phone Number ID</label>
              <Input value={fields.whatsapp_phone_number_id || ""} onChange={e => set("whatsapp_phone_number_id", e.target.value)} placeholder="1234567890" data-testid="input-wa-phone-id" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Access Token</label>
              <Input type="password" value={fields.whatsapp_access_token || ""} onChange={e => set("whatsapp_access_token", e.target.value)} placeholder={isConfigured(settings?.whatsapp_access_token || "") ? "••••••••" : "EAA..."} data-testid="input-wa-token" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Mallnamn</label>
              <Input value={fields.whatsapp_template_name || ""} onChange={e => set("whatsapp_template_name", e.target.value)} placeholder="delivery_note" data-testid="input-wa-template" />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Språkkod</label>
              <Input value={fields.whatsapp_language_code || ""} onChange={e => set("whatsapp_language_code", e.target.value)} placeholder="sv" data-testid="input-wa-lang" />
            </div>
          </div>
          <Button size="sm" onClick={() => save(["whatsapp_business_account_id","whatsapp_phone_number_id","whatsapp_access_token","whatsapp_template_name","whatsapp_language_code"])} disabled={saveMutation.isPending} data-testid="button-save-whatsapp">
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Spara WhatsApp
          </Button>
          <div className="pt-3 border-t mt-2">
            <p className="text-sm font-medium mb-2">Testa WhatsApp</p>
            <div className="flex gap-2">
              <Input
                value={testWa}
                onChange={e => setTestWa(e.target.value)}
                placeholder="+46701234567"
                className="max-w-xs"
                data-testid="input-test-wa"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => testWaMutation.mutate()}
                disabled={testWaMutation.isPending || !testWa}
                data-testid="button-send-test-wa"
              >
                {testWaMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Skicka test
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ExternalApiKeyCard() {
  const { toast } = useToast();
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);

  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["/api/admin/settings"],
  });

  useEffect(() => {
    if (settings?.external_api_key) {
      setApiKey(settings.external_api_key);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (key: string) => apiRequest("POST", "/api/admin/settings", { external_api_key: key }),
    onSuccess: () => {
      toast({ title: "API-nyckel sparad!" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte spara API-nyckel.", variant: "destructive" });
    },
  });

  const generateKey = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "ratt_";
    for (let i = 0; i < 40; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setApiKey(key);
    setShowKey(true);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5" />
          Externt API
        </CardTitle>
        <CardDescription>
          API-nyckel för externa system (t.ex. leveransapp). Används med header: X-API-Key
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">API-nyckel</label>
          <div className="flex gap-2">
            <Input
              type={showKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Generera eller ange en API-nyckel"
              data-testid="input-external-api-key"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowKey(!showKey)}
              data-testid="button-toggle-api-key"
            >
              {showKey ? "Dölj" : "Visa"}
            </Button>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={generateKey}
            data-testid="button-generate-api-key"
          >
            <Key className="h-4 w-4 mr-2" />
            Generera nyckel
          </Button>
          <Button
            type="button"
            onClick={() => saveMutation.mutate(apiKey)}
            disabled={saveMutation.isPending || !apiKey}
            data-testid="button-save-api-key"
          >
            {saveMutation.isPending ? "Sparar..." : "Spara"}
          </Button>
        </div>
        {apiKey && (
          <div className="bg-muted rounded-md p-3 space-y-2">
            <p className="text-xs text-muted-foreground font-medium">Användning för externa system:</p>
            <code className="text-xs block bg-background p-2 rounded">
              PATCH /api/external/orders/:id/status<br />
              Header: X-API-Key: {showKey ? apiKey : "••••••••••"}<br />
              Body: {`{ "status": "delivered" }`}
            </code>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PushNotificationCard() {
  const { toast } = useToast();

  const { data: vapidData, isLoading } = useQuery<{ publicKey: string | null }>({
    queryKey: ["/api/push/vapid-key"],
  });

  const generateMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/admin/push/generate-vapid"),
    onSuccess: () => {
      toast({ title: "VAPID-nycklar genererade" });
      queryClient.invalidateQueries({ queryKey: ["/api/push/vapid-key"] });
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte generera nycklar", variant: "destructive" });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push-notifikationer
        </CardTitle>
        <CardDescription>Konfigurera push-notifikationer till kunder</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-10 w-full" />
        ) : vapidData?.publicKey ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">Aktiv</Badge>
              <span className="text-sm text-muted-foreground">VAPID-nycklar konfigurerade</span>
            </div>
            <div className="bg-muted rounded-md p-3">
              <p className="text-xs text-muted-foreground mb-1">Publik nyckel</p>
              <p className="text-xs font-mono break-all">{vapidData.publicKey}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              data-testid="button-regenerate-vapid"
            >
              <Key className="h-4 w-4 mr-2" />
              {generateMutation.isPending ? "Genererar..." : "Generera nya nycklar"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              VAPID-nycklar behövs för att skicka push-notifikationer till kundernas enheter.
            </p>
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={generateMutation.isPending}
              data-testid="button-generate-vapid"
            >
              <Key className="h-4 w-4 mr-2" />
              {generateMutation.isPending ? "Genererar..." : "Generera VAPID-nycklar"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
